<template>
  <div class="channels-card">
    <div class="channels-card__head">
      <div class="channels-card__title">{{ $t('salesChannels') }}</div>
      <div
        class="channels-card__status"
        :class="{ 'is-on': connectedCount }"
      >
        {{ connectedCount }} {{ $t('connected') }}
      </div>
    </div>
    <div class="channels-card__list">
      <div
        v-for="item in channels"
        :key="item.key"
        class="channels-card__row"
      >
        <img
          v-if="item.img"
          :src="item.img"
          class="channels-card__logo"
          :alt="item.label"
        >
        <span
          v-else
          class="channels-card__logo channels-card__logo--fallback"
        >
          {{ item.label.charAt(0) }}
        </span>
        <span class="channels-card__meta">
          <span class="channels-card__top">
            <span class="channels-card__name">{{ item.label }}</span>
            <span
              class="channels-card__state"
              :class="item.connected ? 'is-on' : 'is-off'"
            >
              {{ item.connected ? $t('connected') : $t('offline') }}
            </span>
          </span>
          <span class="channels-card__stats">
            <span>{{ $t('order') }} {{ $numberFormat(statOf(item, 'orders')) }}</span>
            <span class="channels-card__dot">·</span>
            <span>{{ $t('sell') }} {{ $numberFormat(statOf(item, 'sell')) }} {{ $store.state.currency.name }}</span>
          </span>
        </span>
      </div>
      <div
        v-if="!channels.length"
        class="channels-card__empty"
      >
        <home-empty-state :size="80" />
      </div>
    </div>
    <button
      type="button"
      class="channels-card__add"
      @click="goIntegrator"
    >
      + {{ $t('connectNewChannel') }}
    </button>
  </div>
</template>

<script>
import HomeEmptyState from './HomeEmptyState.vue'

export default {
  name: 'HomeSalesChannels',
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: '7days'
    }
  },
  data () {
    return {
      stats: {}
    }
  },
  computed: {
    channels () {
      const list = [
        {
          key: 'digikala',
          label: this.$t('digikala'),
          img: require('@/assets/digikala.png'),
          allowed: this.digikalaPermission,
          connected: this.isOn(this.$store.state.digikala)
        },
        {
          key: 'basalam',
          label: this.$t('basalam'),
          img: require('@/assets/basalam.png'),
          allowed: this.baSalamPermission,
          connected: this.isOn(this.$store.state.basalam)
        },
        {
          key: 'woocommerce',
          label: this.$t('woocommerce'),
          img: null,
          allowed: this.woocommercePermission,
          connected: this.isOn(this.$store.state.woocommerce)
        },
        {
          key: 'sazito',
          label: this.$t('sazito'),
          img: null,
          allowed: this.sazitoPermission,
          connected: this.isOn(this.$store.state.sazito)
        }
      ]
      return list.filter(item => item.allowed)
    },
    connectedCount () {
      return this.channels.filter(item => item.connected).length
    }
  },
  watch: {
    dateType () {
      this.fetchStats()
    }
  },
  mounted () {
    this.fetchStats()
  },
  methods: {
    statOf (item, field) {
      const row = this.stats[item.key] || {}
      const raw = field === 'orders'
        ? (row.orders != null ? row.orders : row.orderCount)
        : (row.sell != null ? row.sell : row.amount)
      return parseFloat(raw) || 0
    },
    pickStats (data) {
      const payload = data && data.data && !Array.isArray(data.data) ? data.data : data
      const map = {}
      if (Array.isArray(payload)) {
        payload.forEach(row => {
          const key = row.key || row.channel || row.name
          if (key) map[key] = row
        })
        return map
      }
      if (payload && typeof payload === 'object') return payload
      return {}
    },
    fetchStats () {
      this.$axios.get(this.$endpoint + 'widgets', {
        params: {
          type: 'salesChannels',
          dateType: this.dateType
        }
      }).then(res => {
        this.stats = this.pickStats(res.data)
      }).catch(() => {
        this.stats = {}
      })
    },
    isOn (value) {
      return !!(value && value !== 'inactive' && value !== 'deactive' && value !== 'off')
    },
    goIntegrator () {
      this.$router.push({ name: 'settingIntegrator' }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.channels-card {
  width: 100%;
  height: 100%;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 4px;
  padding: 12px 14px;
  min-height: 292px;
  max-height: 292px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.channels-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  flex-shrink: 0;
}
.channels-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.channels-card__status {
  font-size: 12px;
  color: #9CA3AF;
}
.channels-card__status.is-on {
  color: #00A63E;
}
.channels-card__list {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.channels-card__list::-webkit-scrollbar {
  width: 4px;
}
.channels-card__list::-webkit-scrollbar-thumb {
  background: #D1D5DB;
  border-radius: 4px;
}
.channels-card__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F3F4F6;
}
.channels-card__row:last-child {
  border-bottom: none;
}
.channels-card__logo {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  object-fit: contain;
  flex-shrink: 0;
  background: #F9FAFB;
}
.channels-card__logo--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
}
.channels-card__meta {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.channels-card__top {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.channels-card__name {
  font-size: 12px;
  color: #111;
  font-weight: 700;
}
.channels-card__state {
  font-size: 11px;
}
.channels-card__stats {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 11px;
  color: #6B7280;
  line-height: 1.4;
}
.channels-card__dot {
  color: #D1D5DB;
}
.channels-card__state.is-on {
  color: #00A63E;
}
.channels-card__state.is-off {
  color: #9CA3AF;
}
.channels-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6B7280;
  text-align: center;
}
.channels-card__add {
  margin-top: 8px;
  flex-shrink: 0;
  width: 100%;
  height: 34px;
  border: 1px dashed #D1D5DB;
  background: #fff;
  border-radius: 4px;
  font-size: 12px;
  color: #6B7280;
  cursor: pointer;
}
</style>
